/**
 * Searching and counting a `<TreeChart>`.
 *
 * Both walk the whole tree, which is what makes filtering in memory correct here rather
 * than the drift CLAUDE.md forbids: the rule against client-side filtering is about a
 * **paged** list, where the filter would quietly apply to the rows on screen and not the
 * ones behind them. A tree arrives whole from one call, so there is nothing behind it.
 */

interface TreeShape<T> {
  children: T[];
}

/**
 * Keeps the nodes that match, and the ancestors that lead to them.
 *
 * A matching node keeps its **entire** subtree, because a hit is usually the branch the
 * reader was looking for and stripping its children would answer "where is Accounting" with
 * a card that no longer says what sits under it. A node that does not match itself is kept
 * only as a path to one that does, and then only with the children that lead there.
 */
export function filterTree<T extends TreeShape<T>>(
  nodes: readonly T[],
  matches: (node: T) => boolean,
): T[] {
  const kept: T[] = [];

  for (const node of nodes) {
    if (matches(node)) {
      kept.push(node);
      continue;
    }

    const children = filterTree(node.children, matches);

    if (children.length > 0) {
      kept.push({ ...node, children });
    }
  }

  return kept;
}

/** Every node in the tree, at every depth — what the record count reports. */
export function countTree<T extends TreeShape<T>>(nodes: readonly T[]): number {
  return nodes.reduce((total, node) => total + 1 + countTree(node.children), 0);
}
